import React, { useEffect, useMemo, useState } from 'react'

import {
  number,
  string,
  bool,
  array,
  shape,
  oneOfType,
  arrayOf,
  oneOf,
  object,
} from 'prop-types'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import DescriptionArea from 'Pods/Shared/ItemPageComponents/DescriptionArea/DescriptionArea'
import GalleryArea from 'Pods/Shared/ItemPageComponents/GalleryArea/GalleryArea'
import { collectionModelsAreaMock } from 'Pods/Shared/ItemPageComponents/Mocks/collectionModelsArea'
import { descriptionAreaMockedData } from 'Pods/Shared/ItemPageComponents/Mocks/descriptionArea'
import { galleryAreaMockedData } from 'Pods/Shared/ItemPageComponents/Mocks/galleryArea'
import { reviewsAreaMockedData } from 'Pods/Shared/ItemPageComponents/Mocks/reviewsArea'
import { useCasesAreaMockedData } from 'Pods/Shared/ItemPageComponents/Mocks/useCasesArea'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import useGA4ItemTracking from '../Hooks/useGA4ItemTracking'
import CollectionModelsArea from './Components/CollectionModelsArea/CollectionModelsArea'
import DetailsAreaWrapper from './Components/DetailsAreaWrapper/DetailsAreaWrapper'
import PricingAreaWrapper from './Components/PricingAreaWrapper/PricingAreaWrapper'
import ReviewsAreaWrapper from './Components/ReviewsAreaWrapper/ReviewsAreaWrapper'
import SellerAreaWrapper from './Components/SellerAreaWrapper/SellerAreaWrapper'
import UseCasesAreaWrapper from './Components/UseCasesAreaWrapper/UseCasesAreaWrapper'
import { TopSectionPropsProvider } from './context/TopSectionPropsContext'

function TopSection({
  isCollection = false,
  collectionModelsArea,
  sellerArea,
  reviewsArea,
  pricingArea,
  descriptionArea,
  detailsArea,
  galleryArea,
  useCasesArea,
  badgesArea = EMPTY_ARRAY,
  locale = 'en',
  extractingCoreStyles,
  showCgtVerification = false,
}) {
  const pageConfig = usePageConfig()
  const [hasCheckedUnderControl, setHasCheckedUnderControl] = useState(false)

  const reviewsAreaData = extractingCoreStyles ? reviewsAreaMockedData : reviewsArea
  const descriptionAreaData = extractingCoreStyles ? descriptionAreaMockedData : descriptionArea
  const galleryAreaData = extractingCoreStyles ? galleryAreaMockedData : galleryArea
  const useCasesAreaData = extractingCoreStyles ? useCasesAreaMockedData : useCasesArea
  const collectionModelsAreaData = extractingCoreStyles ? collectionModelsAreaMock : collectionModelsArea

  // Finds a relevant tag to link feature tags (Animated, Rigged, Low-poly) to category pages
  const tagForLinking = (() => {
    const tags = descriptionAreaData.relatedTags
    const excludedTags = ['animated', 'animation', 'rigged', 'rigg', 'low-poly', 'lowpoly', 'low poly']

    return tags.find((tag) => {
      const isExcluded = excludedTags.some((excludedTag) =>
        tag?.text.toLowerCase().includes(excludedTag)
      )
      const includes3d = tag?.text.toLowerCase().includes('3d')

      return !isExcluded && !includes3d && tag.active
    })
  })()?.text.split(' ').join('-')

  const detailsAreaData = { ...detailsArea, tagForLinking }

  useGA4ItemTracking()

  useEffect(() => {
    if (hasCheckedUnderControl) {
      return
    }

    if (pageConfig) {
      const { isUnderControl, currentUserProfile, currentUserUsername } = pageConfig

      window.checkIfUnderControl(
        isUnderControl,
        currentUserProfile,
        currentUserUsername,
      )

      setHasCheckedUnderControl(true)
    }
  }, [pageConfig, hasCheckedUnderControl])

  const is3dPrintModel = Boolean(useCasesAreaData.printReady)
  const currentUsername = pageConfig?.currentUserUsername
  const isCurrentUserAdmin = pageConfig?.currentUserIsAdmin
  const showUseCasesSection = useMemo(() => pageConfig?.showUseCasesSection, [pageConfig])
  const showUseCasesFor3dModels = useMemo(() => pageConfig?.showUseCasesFor3dModels, [pageConfig])
  const hasMakes = useCasesAreaData.itemUseCaseCount > 0
  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === 'xs'

  const role = useMemo(() => {
    const currentUserUsername = pageConfig?.currentUserUsername?.toLowerCase()
    const authorUsername = pageConfig?.product?.authorUsername?.toLowerCase()

    if (!currentUserUsername) {
      return 'guest'
    }

    if (currentUserUsername === authorUsername) {
      return 'seller'
    }

    if (pageConfig?.product?.isPurchased) {
      return 'buyer'
    }

    return 'user'
  }, [pageConfig])

  const topSectionPropsValue = useMemo(() => ({
    is3dPrintModel,
    currentUsername,
    role,
    isMobile,
    pageConfig,
    useCasesAreaData,
  }), [is3dPrintModel, currentUsername, role, isMobile, pageConfig, useCasesAreaData])

  return (
    <TopSectionPropsProvider value={topSectionPropsValue}>
      <div className={`item-page__body ${hasMakes && !extractingCoreStyles ? 'has-makes' : ''}`}>
        <GalleryArea
          {...galleryAreaData}
          extractingCoreStyles={extractingCoreStyles}
        />

        <div className="item-page__left-panel">
          {(isCollection || extractingCoreStyles) && (
            <div className="collection-models-area-wrapper">
              <CollectionModelsArea {...collectionModelsAreaData} />
            </div>
          )}

          <div className="description-area-wrapper">
            <DescriptionArea
              {...descriptionAreaData}
              canDeleteTags={isCurrentUserAdmin}
              extractingCoreStyles={extractingCoreStyles}
            />
          </div>

          <div className="use-cases-area-wrapper">
            <UseCasesAreaWrapper
              useCasesAreaData={useCasesAreaData}
              role={role}
              currentUsername={currentUsername}
              extractingCoreStyles={extractingCoreStyles}
              showUseCasesSection={showUseCasesSection}
              showUseCasesFor3dModels={showUseCasesFor3dModels}
            />
          </div>

          <div className="reviews-area-wrapper">
            <ReviewsAreaWrapper
              reviewsAreaData={reviewsAreaData}
              currentUsername={currentUsername}
              isCurrentUserAdmin={isCurrentUserAdmin}
              role={role}
              canReview={pageConfig?.product?.canReview}
              locale={locale}
              extractingCoreStyles={extractingCoreStyles}
              productOwnerUsername={sellerArea.designerName}
            />
          </div>
        </div>

        <div className="item-page__right-panel">
          <div className="pricing-area-wrapper">
            <PricingAreaWrapper
              pricingAreaData={pricingArea}
              badgesArea={badgesArea}
              extractingCoreStyles={extractingCoreStyles}
              locale={locale}
            />
          </div>

          <div className="seller-area-wrapper">
            <SellerAreaWrapper
              sellerAreaData={sellerArea}
              extractingCoreStyles={extractingCoreStyles}
            />
          </div>

          <div className="details-area-wrapper">
            <DetailsAreaWrapper
              detailsAreaData={detailsAreaData}
              extractingCoreStyles={extractingCoreStyles}
              showCgtVerification={showCgtVerification}
              locale={locale}
            />
          </div>
        </div>
      </div>
    </TopSectionPropsProvider>
  )
}

TopSection.propTypes = {
  isCollection: bool,
  locale: string,
  sellerArea: shape({
    designerId: number.isRequired,
    designerName: string.isRequired,
    avatarUrl: string,
    rating: number.isRequired,
    reviewsCount: number.isRequired,
    availableForHire: bool.isRequired,
  }).isRequired,
  reviewsArea: shape({
    commentsCount: number.isRequired,
    comments: arrayOf(shape({
      id: number.isRequired,
      username: string.isRequired,
      content: string.isRequired,
      updatedAt: string.isRequired,
    })).isRequired,
    likeCount: number.isRequired,
    dislikeCount: number.isRequired,
    ratings: arrayOf(shape({
      anonymous: bool,
      authorId: number,
      automatedReview: bool,
      avatar: shape({
        small: shape({
          url: string,
        }),
        url: string,
      }),
      comment: string,
      createdAt: string,
      id: number,
      itemId: number,
      score: number,
      updatedAt: string,
      userId: number,
      username: string,
      userRole: oneOf(['seller', 'multiple_buyer', 'buyer', 'community_member']),
    })).isRequired,
    hasMoreComments: bool.isRequired,
    hasMoreRatings: bool.isRequired,
  }).isRequired,
  pricingArea: shape({
    title: string.isRequired,
    price: string.isRequired,
    free: bool.isRequired,
    license: oneOf([
      'custom',
      'custom_no_ai',
      'editorial',
      'editorial_no_ai',
      'editorial_royalty_free',
      'royalty_free',
      'royalty_free_no_ai',
    ]).isRequired,
    reviewsCount: number.isRequired,
    ratingScore: number.isRequired,
    customLicense: string,
    publishedAiFriendly: bool,
    productId: number.isRequired,
  }).isRequired,
  descriptionArea: shape({
    description: string.isRequired,
    descriptionAi: string,
    aiDescription: bool,
    relatedTags: arrayOf(shape({
      active: bool,
      id: number.isRequired,
      text: string.isRequired,
      url: string,
    })).isRequired,
    breadcrumbs: arrayOf(shape({
      title: string.isRequired,
      url: string.isRequired,
      isActive: bool.isRequired,
    })).isRequired,
  }).isRequired,
  detailsArea: shape({
    collections: arrayOf(shape({
      collectionPrice: number.isRequired,
      id: number.isRequired,
      itemsCount: number.isRequired,
      saleOffDiscount: number.isRequired,
      thumbnail: string.isRequired,
      title: string.isRequired,
      url: string.isRequired,
    })).isRequired,
    details: oneOfType([
      shape({
        animated: bool,
        rigged: bool,
        game_ready: bool,
        pbr: bool,
        textures: bool,
        materials: bool,
        uv_mapping: bool,
        plugins_used: bool,
      }),
      arrayOf(shape({
        title: string,
        value: oneOfType([string, number, bool]),
      })),
    ]),
    publishDate: string,
    id: number.isRequired,
    geometryType: oneOfType([string, object]),
    polygons: oneOfType([number, string]),
    vertices: oneOfType([number, string]),
    cgtVerificationData: shape({
      data: shape({
        files: shape({
          fbxFormat: shape({
            pass: bool,
          }),
        }),
        geometry: shape({
          facetedGeometry: shape({
            pass: bool,
          }),
          noNgons: shape({
            pass: bool,
          }),
          nonManifoldVertices: shape({
            pass: bool,
          }),
        }),
        naming: shape({
          allowedCharacters: shape({
            pass: bool,
          }),
        }),
        scene: shape({
          dccObjectTypes: shape({
            pass: bool,
          }),
        }),
        texturesAndMaterials: shape({
          embeddedTextures: shape({
            pass: bool,
          }),
          missingRequiredTextures: shape({
            pass: bool,
          }),
          numberOfMaterialsPerObject: shape({
            pass: bool,
          }),
          textureAspectRatio: shape({
            pass: bool,
          }),
          textureResolutionNotPowerOf2: shape({
            pass: bool,
          }),
        }),
        uV: shape({
          numberOfUVs: shape({
            pass: bool,
          }),
          overlappedUvPerUVIsland: shape({
            pass: bool,
          }),
        }),
      }),
      reportType: string,
      score: number,
    }),
    printModelVerificationData: shape({
      cleanGeometry: shape({
        manifoldGeometry: bool.isRequired,
        noSelfIntersections: bool.isRequired,
        noOverlappingFaces: bool.isRequired,
        noZeroAngledCorners: bool.isRequired,
        noZeroLengthEdges: bool.isRequired,
        noZeroAreaFaces: bool.isRequired,
        noBadContiguousEdges: bool.isRequired,
        noOpenEdges: bool.isRequired,
      }),
      score: number.isRequired,
    }),
    nativeFormats: arrayOf(shape({
      extensionPath: string,
      fileSize: string.isRequired,
      title: string.isRequired,
      version: string,
      rendererName: string,
      rendererVersion: oneOfType([string, number]),
    })),
    exchangeFormats: arrayOf(shape({
      extensionPath: string,
      fileSize: string.isRequired,
      title: string.isRequired,
      version: string,
      rendererName: string,
      rendererVersion: oneOfType([string, number]),
    })),
    ungroupedFormats: arrayOf(shape({
      extensionPath: string,
      fileSize: string.isRequired,
      title: string.isRequired,
      version: string,
      rendererName: string,
      rendererVersion: oneOfType([string, number]),
    })),
    units: string,
  }).isRequired,
  galleryArea: shape({
    medias: array.isRequired,
    title: string.isRequired,
  }).isRequired,
  useCasesArea: shape({
    printReady: bool.isRequired,
    itemUseCaseCount: number.isRequired,
  }).isRequired,
  badgesArea: arrayOf(oneOf([
    'BEST PRICE GUARANTEED',
    'NEW',
    'TOP SELLING',
    'QUALITY VERIFIED',
    '3D PRINT VERIFIED',
    'TOP RATED',
    'AI GENERATED',
    'DESIGNER\'S PICK',
  ])),
  collectionModelsArea: shape({
    products: array.isRequired,
    numberOfModels: number.isRequired,
    slug: string.isRequired,
    next: bool,
  }).isRequired,
  extractingCoreStyles: bool.isRequired,
  showCgtVerification: bool,
}

export default TopSection
